/**
 * useMilestoneEventsStore, Supabase-backed CRUD for the Milestones tool.
 *
 * Mirrors iOS app (frontend/src/services/database.ts:257-372 and
 * app/tools/milestones.tsx) so cross-device sync just works:
 *
 *   • Table: `milestone_events`
 *     columns: id, user_id, milestone_name, milestone_start_date,
 *              milestone_end_date, status ('pending' | 'in-progress' |
 *              'completed'), notes, build_type, created_at, updated_at
 *   • Filter by `user_id` AND optional `build_type` so a multi-project
 *     user keeps their New Home / Shed / etc. milestone lists separate.
 *   • Realtime subscription so adds/edits on iOS appear live on desktop.
 *
 * Returns:
 *   { milestones, loading, addMilestone, updateMilestone,
 *     updateStatus, deleteMilestone, refresh }
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

export const useMilestoneEventsStore = (buildType) => {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMilestones = useCallback(async () => {
    const supabase = getSupabase();
    if (!user?.id || !supabase) {
      setMilestones([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let q = supabase
      .from('milestone_events')
      .select('*')
      .eq('user_id', user.id);
    if (buildType) q = q.eq('build_type', buildType);
    const { data, error } = await q.order('created_at', { ascending: true });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('milestone_events fetch error', error);
      setMilestones([]);
    } else {
      setMilestones(
        (data || []).map((m) => ({
          ...m,
          status: m.status || 'pending',
        }))
      );
    }
    setLoading(false);
  }, [user?.id, buildType]);

  useEffect(() => { fetchMilestones(); }, [fetchMilestones]);

  // Realtime sync (cross-device, cross-platform).
  //
  // NOTE: we intentionally do NOT pass `filter: user_id=eq.X` on this
  // subscription. Supabase's default replica identity is `DEFAULT`
  // (primary key only), which means DELETE events don't carry the
  // `user_id` column, so the client-side filter would silently drop
  // every DELETE replication event, leaving phantom rows on screen when
  // the iOS app or another browser tab deleted a milestone.
  // Row-Level Security already restricts SELECT/INSERT/UPDATE/DELETE on
  // `milestone_events` to `auth.uid() = user_id`, so the server only
  // sends us events for our own rows anyway, the filter was redundant.
  //
  // Channel name is suffixed with a per-instance unique id so React 19
  // effect re-runs (deps change, dev double-invoke, fast refresh) never
  // hit Supabase's "cannot add postgres_changes callbacks after
  // subscribe()" error, `supabase.channel(name)` reuses an existing
  // channel by name, and once subscribed it rejects further `.on()`
  // calls. A unique suffix forces a fresh channel every mount.
  const channelSuffix = useRef(Math.random().toString(36).slice(2, 8));
  useEffect(() => {
    const supabase = getSupabase();
    if (!user?.id || !supabase) return undefined;
    const channel = supabase
      .channel(`milestone_events:${user.id}:${channelSuffix.current}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'milestone_events',
      }, () => { fetchMilestones(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, fetchMilestones]);

  const addMilestone = useCallback(async ({ name, startDate, endDate, notes }) => {
    const supabase = getSupabase();
    if (!user?.id || !supabase || !name?.trim()) return null;
    const payload = {
      user_id: user.id,
      milestone_name: name.trim(),
      milestone_start_date: startDate || null,
      milestone_end_date: endDate || null,
      notes: notes?.trim() || null,
      status: 'pending',
      ...(buildType ? { build_type: buildType } : {}),
    };
    const { data, error } = await supabase
      .from('milestone_events')
      .insert(payload)
      .select()
      .single();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('addMilestone error', error);
      return null;
    }
    setMilestones((prev) => [...prev, data]);
    return data;
  }, [user?.id, buildType]);

  const updateMilestone = useCallback(async (id, patch) => {
    const supabase = getSupabase();
    if (!id || !supabase) return false;
    const dbPatch = {
      ...(patch.name !== undefined ? { milestone_name: patch.name } : {}),
      ...(patch.startDate !== undefined ? { milestone_start_date: patch.startDate || null } : {}),
      ...(patch.endDate !== undefined ? { milestone_end_date: patch.endDate || null } : {}),
      ...(patch.notes !== undefined ? { notes: patch.notes || null } : {}),
      ...(patch.status !== undefined ? { status: patch.status } : {}),
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from('milestone_events')
      .update(dbPatch)
      .eq('id', id)
      .select()
      .single();
    if (error) return false;
    setMilestones((prev) => prev.map((m) => (m.id === id ? data : m)));
    return true;
  }, []);

  const updateStatus = useCallback((id, status) =>
    updateMilestone(id, { status }), [updateMilestone]);

  const deleteMilestone = useCallback(async (id) => {
    const supabase = getSupabase();
    if (!id || !supabase) return false;
    const { error } = await supabase
      .from('milestone_events')
      .delete()
      .eq('id', id);
    if (error) return false;
    setMilestones((prev) => prev.filter((m) => m.id !== id));
    return true;
  }, []);

  return {
    milestones,
    loading,
    addMilestone,
    updateMilestone,
    updateStatus,
    deleteMilestone,
    refresh: fetchMilestones,
  };
};

// ─── Phase classification (must match iOS milestones.tsx 171-256) ──
export const PRE_CONSTRUCTION_MILESTONES = {
  'New Home': [
    'Signed up to a builder', 'Construction Drawings/Master Plans',
    'Engineering', 'Energy Rating', 'Building Costs',
    'Council Approvals', 'Selections',
  ],
  'Renovation': [
    'Signed up to a builder', 'Site Measure and Plans Reviewed',
    'Renovation Drawings/Updated Layout', 'Engineering (if structural)',
    'Energy Rating (if required)', 'Final Building Costs',
    'Council Approval (if required)', 'Selections',
  ],
  'Home Improvement': [
    'Signed up to a contractor', 'Design/Plans',
    'Engineering (if required)', 'Final Building Costs',
    'Council Approval (if required)', 'Selections',
  ],
};

export const CONSTRUCTION_MILESTONES = {
  'New Home': [
    'Slab Pour', 'Frame Stage', 'Lockup Stage', 'First Fix',
    'Insulation and Linings', 'Second Fix', 'Practical Completion',
    'Living in - First 30 Days', 'Maintenance Period',
  ],
  'Renovation': [
    'Demolition', 'Structural Changes/Framing',
    'Rough In (Plumbing & Electrical)', 'Insulation & Linings',
    'Waterproofing (wet areas)', 'Cabinetry & Tiling', 'Second Fix',
    'Practical Completion', 'Living in - First 30 Days',
    'Maintenance Period',
  ],
  'Home Improvement': [
    'Site Preparation', 'Footings/Base/Slab', 'Installation Stage',
    'Services Connection (if required)', 'Finishing Stage',
    'Practical Completion', 'First 30 Days', 'Maintenance Period',
  ],
};

export const QUICK_ADD_MILESTONES = [
  'Site Preparation', 'Foundation', 'Framing', 'Roofing', 'Windows & Doors',
  'Plumbing Rough-In', 'Electrical Rough-In', 'Insulation', 'Drywall',
  'Interior Painting', 'Flooring', 'Cabinetry', 'Final Plumbing',
  'Final Electrical', 'Final Inspection', 'Handover',
];

export const getMilestonePhase = (milestoneName, buildType) => {
  const buildKey = buildType || 'New Home';
  const pre = PRE_CONSTRUCTION_MILESTONES[buildKey] || PRE_CONSTRUCTION_MILESTONES['New Home'];
  const cons = CONSTRUCTION_MILESTONES[buildKey] || CONSTRUCTION_MILESTONES['New Home'];
  const n = (milestoneName || '').toLowerCase();
  if (pre.some((m) => m.toLowerCase() === n)) return 'pre-construction';
  if (cons.some((m) => m.toLowerCase() === n)) return 'construction';
  // keyword heuristic, matches iOS milestones.tsx 250-256
  if (/(plan|approval|design|engineering|selection|cost|signed|energy)/.test(n)) {
    return 'pre-construction';
  }
  return 'construction';
};
